import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "@mdi/react";
import {
  mdiClose,
  mdiTruckFastOutline,
  mdiCheckCircleOutline,
  mdiEye,
  mdiAlertCircleOutline,
} from "@mdi/js";
import { fetchGet, fetchPut } from "../../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

interface SyncResultRow {
  outcome: string;
  outward_id: string;
  order_id?: string;
  type?: string;
  tracking_id?: string;
  tracking_status?: string;
  to_status?: string;
  pod_url?: string;
  pod_filename?: string;
  message?: string;
}

interface SyncSummary {
  total: number;
  delivered: number;
  in_progress: number;
  pod_pending: number;
  skipped: number;
  error: number;
}

type Phase = "confirm" | "processing" | "done";

const SYNC_BASE_URL = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/delhivery`;

const POLL_INTERVAL_MS = 3000;
// A full run is one rate-limited carrier call per shipment plus a POD download
// per delivery, so it can take minutes. Stop following it well after that; the
// run itself continues server-side regardless.
const MAX_POLL_MS = 20 * 60 * 1000;

const DelhiverySyncAllModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const [phase, setPhase] = useState<Phase>("confirm");
  const [results, setResults] = useState<SyncResultRow[]>([]);
  const [summary, setSummary] = useState<SyncSummary | null>(null);
  const [since, setSince] = useState<string | null>(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });

  // The run is server-side, so a reopened modal starts a fresh confirm rather
  // than showing the previous run's table.
  useEffect(() => {
    if (!open) {
      setPhase("confirm");
      setResults([]);
      setSummary(null);
      setSince(null);
      setProgress({ processed: 0, total: 0 });
    }
  }, [open]);

  // Returns true once the run has finished and polling should stop.
  const pollProgress = useCallback(async (runSince: string) => {
    const res = await fetchGet(
      `${SYNC_BASE_URL}/sync-progress?since=${encodeURIComponent(runSince)}`,
    );
    if (!res?.success) return false; // transient — keep following the run

    const data = res.data ?? {};
    setProgress({ processed: data.processed ?? 0, total: data.total ?? 0 });
    setResults(data.results ?? []);
    setSummary(data.summary ?? null);
    return !data.running;
  }, []);

  useEffect(() => {
    if (!open || phase !== "processing" || !since) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      let finished = false;
      try {
        finished = await pollProgress(since);
      } catch (error: any) {
        console.error("Delhivery sync progress poll failed:", error);
      }
      if (cancelled) return;

      if (finished) {
        setPhase("done");
        onSuccess?.();
        return;
      }
      if (Date.now() - startedAt > MAX_POLL_MS) {
        showToast({
          type: ToastType.warning,
          text: "Sync is still running. Reopen this window later to see the rest.",
        });
        setPhase("done");
        onSuccess?.();
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, since, pollProgress]);

  if (!open) return null;

  const handleConfirm = async () => {
    try {
      setPhase("processing");
      const res = await fetchPut(`${SYNC_BASE_URL}/sync-all`, {});

      if (!res?.success) {
        showToast({
          type: ToastType.error,
          text: res?.error || "Failed to sync Delhivery shipments",
        });
        setPhase("confirm");
        return;
      }

      // Progress is matched against the server's run timestamp, so a missing one
      // cannot be substituted with the browser clock without silently returning
      // an empty run.
      if (!res.data?.run_started_at) {
        showToast({
          type: ToastType.error,
          text: "Sync started but no run timestamp was returned — cannot track progress.",
        });
        setPhase("confirm");
        return;
      }

      if (res.data.already_running) {
        showToast({
          type: ToastType.info,
          text: "A sync was already running — showing its progress.",
        });
      }

      // The run continues in the background; progress is followed by polling.
      setSince(res.data.run_started_at);
    } catch (error: any) {
      console.error("Delhivery sync-all failed:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Failed to sync Delhivery shipments",
      });
      setPhase("confirm");
    }
  };

  // Show delivered first, then other changed states.
  const orderedResults = [...results].sort((a, b) =>
    a.outcome === "delivered" ? -1 : b.outcome === "delivered" ? 1 : 0,
  );

  const outcomeLabel = (r: SyncResultRow) => {
    switch (r.outcome) {
      case "delivered":
        return "Delivered";
      case "pod_pending":
        return "Delivered (POD pending)";
      case "in_progress":
        return r.tracking_status || "In transit";
      case "skipped":
        return "Skipped";
      case "error":
        return "Failed";
      default:
        return r.outcome;
    }
  };

  return createPortal(
    <div
      id="warehouse-outward-delhivery-sync-all-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-4xl my-auto flex flex-col rounded-xl bg-white shadow-xl overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <Icon
              path={mdiTruckFastOutline}
              size={0.95}
              className="text-blue-600"
            />
            <div>
              <h2 className="text-lg font-semibold">Sync All Delhivery</h2>
              <p className="text-xs text-gray-500">
                Pull latest status for all dispatched Delhivery shipments
              </p>
            </div>
          </div>
          <button
            id="warehouse-outward-delhivery-sync-close-icon-btn"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
          {phase === "confirm" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700 space-y-2">
                <p>
                  This will fetch the latest status from Delhivery for{" "}
                  <strong>every dispatched SO/ST</strong> shipped via Delhivery.
                </p>
                <p>
                  For each shipment that Delhivery reports as{" "}
                  <strong>Delivered</strong>, its Proof of Delivery (POD) will
                  be downloaded, attached to the SO/ST, and the record will be
                  marked <strong>Delivered</strong>. Shipments still in transit
                  are left unchanged.
                </p>
                <p className="text-xs text-gray-500">
                  This may take a little while depending on how many shipments
                  are in transit.
                </p>
              </div>
            </div>
          )}

          {phase === "processing" && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
              <p className="text-sm text-gray-600">
                Syncing Delhivery shipments and attaching PODs…
              </p>
              {progress.total > 0 && (
                <>
                  <div className="h-1.5 w-64 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-all"
                      style={{
                        width: `${Math.min(
                          100,
                          Math.round(
                            (progress.processed / progress.total) * 100,
                          ),
                        )}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    {progress.processed} of {progress.total} shipments checked
                    {summary && summary.delivered > 0
                      ? ` · ${summary.delivered} delivered`
                      : ""}
                  </p>
                </>
              )}
              <p className="text-xs text-gray-400 text-center max-w-sm">
                This runs on the server — you can close this window and the sync
                will carry on.
              </p>
            </div>
          )}

          {phase === "done" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4">
                <Icon
                  path={mdiCheckCircleOutline}
                  size={0.9}
                  className="text-green-600 shrink-0 mt-0.5"
                />
                <div className="text-sm text-gray-700">
                  {summary && summary.delivered > 0 ? (
                    <p>
                      <strong>{summary.delivered}</strong> shipment
                      {summary.delivered === 1 ? " has" : "s have"} been updated
                      to <strong>Delivered</strong> with POD attached.
                    </p>
                  ) : (
                    <p>No shipments were delivered in this run.</p>
                  )}
                  {summary && (
                    <p className="text-xs text-gray-500 mt-1">
                      {progress.processed} of {progress.total} checked ·{" "}
                      {summary.delivered} delivered · {summary.in_progress} in
                      transit · {summary.pod_pending} POD pending ·{" "}
                      {summary.error} failed
                    </p>
                  )}
                </div>
              </div>

              {orderedResults.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full min-w-max text-sm">
                    <thead className="border-b bg-gray-50">
                      <tr className="text-left text-xs text-gray-500">
                        <th className="p-3">Order / ST</th>
                        <th className="p-3">Type</th>
                        <th className="p-3">Tracking ID</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">POD</th>
                        <th className="p-3">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderedResults.map((r) => (
                        <tr
                          key={r.outward_id}
                          className={`border-b last:border-0 ${
                            r.outcome === "delivered" ? "bg-green-50" : ""
                          }`}
                        >
                          <td className="p-3 font-medium text-gray-900">
                            {r.order_id || r.outward_id}
                          </td>
                          <td className="p-3 text-gray-600">{r.type || "-"}</td>
                          <td className="p-3 text-gray-600">
                            {r.tracking_id || "-"}
                          </td>
                          <td className="p-3">
                            <span
                              className={`rounded-full border px-2.5 py-0.5 text-xs ${
                                r.outcome === "delivered"
                                  ? "border-green-300 bg-green-100 text-green-800"
                                  : r.outcome === "error"
                                    ? "border-red-300 bg-red-100 text-red-700"
                                    : "border-gray-300 bg-gray-100 text-gray-700"
                              }`}
                            >
                              {outcomeLabel(r)}
                            </span>
                          </td>
                          <td className="p-3">
                            {r.pod_url ? (
                              <a
                                id={`warehouse-outward-delhivery-sync-view-pod-${r.outward_id}`}
                                href={r.pod_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
                              >
                                <Icon path={mdiEye} size={0.6} />
                                View POD
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="p-3 text-xs text-gray-500">
                            {r.outcome === "error" && (
                              <Icon
                                path={mdiAlertCircleOutline}
                                size={0.55}
                                className="inline text-red-500 mr-1"
                              />
                            )}
                            {r.message || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-6">
                  No eligible Delhivery shipments to sync.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
          {phase === "confirm" && (
            <>
              <button
                id="warehouse-outward-delhivery-sync-cancel-btn"
                onClick={onClose}
                className="h-10 px-4 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                id="warehouse-outward-delhivery-sync-confirm-btn"
                onClick={handleConfirm}
                className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
              >
                Confirm &amp; Sync
              </button>
            </>
          )}
          {phase === "processing" && (
            <button
              id="warehouse-outward-delhivery-sync-close-keep-syncing-btn"
              onClick={onClose}
              className="h-10 px-4 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Close &amp; keep syncing
            </button>
          )}
          {phase === "done" && (
            <button
              id="warehouse-outward-delhivery-sync-done-close-btn"
              onClick={onClose}
              className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DelhiverySyncAllModal;
